<template>
  <div
    class="fixed top-header left-0 z-10 h-screen w-screen bg-nucleus-lightestBlue"
  >
    <LoadingScreen :loaded="loaded" :message="loadingMessage"></LoadingScreen>

    <div v-if="loaded">
      <HeaderShadow></HeaderShadow>
      <div class="p-4">
        <form @submit.prevent="editSession">
          <div class="flex pb-4">
            <div class="mr-2 w-1/3">
              <router-link
                :to="'/local-admin/sessions/' + $route.params.sessionId"
              >
                <ButtonSecondary
                  type="button"
                  :name="$t('buttons.cancel')"
                  :disabled="loading"
                ></ButtonSecondary>
              </router-link>
            </div>
            <div class="ml-2 w-2/3">
              <Button
                type="submit"
                :name="$t('buttons.save')"
                :disabled="!required(session.name) || loading"
                :loading="loading"
              ></Button>
            </div>
          </div>

          <div
            class="bg-nucleus-white transition-all"
            :class="{ 'opacity-50': loading }"
          >
            <div class="flex items-center px-4 py-6 border-b">
              <div
                class="text-3xl leading-none p-2 mr-6 flex self-center transition bg-nucleus-primaryBlue text-nucleus-white rounded-full icon-session"
              ></div>
              <div class="text-2xl">
                {{ $t("global.defaults.editSession") }}
              </div>
            </div>
            <div class="px-4 py-6 overflow-y-auto scroll-box">
              <Input
                id="name"
                v-model:value="session.name"
                input-type="text"
                :label="$t('inputs.name.label')"
                icon=""
                :max-length="inputMaxLengths.name"
                :placeholder="$t('inputs.name.placeholder')"
                autocomplete="off"
                required
              />
              <Select
                id="ors"
                v-model:value="session.roomId"
                :label="$t('inputs.room.label')"
                :options="operatingRooms"
                required
              />
              <Input
                id="department"
                v-model:value="session.department"
                input-type="text"
                :label="$t('inputs.department.label')"
                icon=""
                :max-length="inputMaxLengths.department"
                :placeholder="$t('inputs.department.placeholder')"
                autocomplete="off"
              />
              <Textarea
                id="notes"
                v-model:value="session.notes"
                :label="$t('inputs.notes.label')"
                :max-length="inputMaxLengths.notes"
                :placeholder="$t('inputs.notes.placeholder')"
              />
            </div>
          </div>
        </form>

        <ErrorBox
          v-if="errorHeading || errorContent"
          :heading="errorHeading"
          :content="errorContent"
          :fixed="errorFixed"
          @closeError="
            errorHeading = '';
            errorContent = '';
          "
        ></ErrorBox>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import HeaderShadow from "@/components/utils/HeaderShadow.vue"; // @ is an alias to /src
import Button from "@/components/utils/Button.vue"; // @ is an alias to /src
import ButtonSecondary from "@/components/utils/ButtonSecondary.vue"; // @ is an alias to /src
import Input from "@/components/utils/Input.vue"; // @ is an alias to /src
import Textarea from "@/components/utils/Textarea.vue"; // @ is an alias to /src
import ListItemBasic from "@/components/utils/ListItemBasic.vue"; // @ is an alias to /src
import Select from "@/components/utils/Select.vue"; // @ is an alias to /src
import LoadingScreen from "@/components/global/LoadingScreen.vue";
import ErrorBox from "@/components/utils/ErrorBox.vue";

import InputMaxLengths from "@/store/data/inputMaxLengths.json";
import { getLocalisedError } from "@/utils/apiUtils";

@Options({
  components: {
    Button,
    ButtonSecondary,
    HeaderShadow,
    Input,
    Textarea,
    ListItemBasic,
    Select,
    LoadingScreen,
    ErrorBox,
  },
  data() {
    return {
      session: {},
      loaded: false,
      loading: false,
      operatingRooms: null,
      loadingMessage: this.$t("global.defaults.loadingSession"),
      errorHeading: "",
      errorContent: "",
      errorFixed: true,
      inputMaxLengths: InputMaxLengths[0],
    };
  },
  beforeMount() {
    this.getSession();
  },
  mounted() {
    document.title =
      this.$t("global.defaults.editSession") +
      " - " +
      this.$t("global.defaults.browserTitle");
    this.getSessionOptions();
  },
  methods: {
    getSession() {
      this.$store
        .dispatch("getSession", this.$route.params.sessionId)
        .then((response: Record<string, unknown>) => {
          if (response.data) {
            this.session = response.data;
            this.loaded = true;
            if (this.session.state === "STARTED") this.startingStopping = false;
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loaded = true;
          this.errorFixed = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },

    editSession() {
      this.loading = true;

      this.session.roomId = Number(this.session.roomId);

      let roomName = "";

      if (this.operatingRooms) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        roomName = this.operatingRooms.filter((room: any) => {
          return room.id === this.session.roomId;
        })[0].name;
      }

      const data = {
        name: this.session.name,
        roomId: this.session.roomId,
        roomName: roomName,
        department: this.session.department,
        notes: this.session.notes,
      };

      console.log(data);

      this.$store
        .dispatch("editSession", { sessionId: this.session.id, data: data })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response) {
            this.loading = false;
            this.$store.dispatch("changeUpdate", "session");
            this.$router.push({
              name: "sessionsDetail",
              params: { sessionId: this.$route.params.sessionId },
            });
            console.log("edit session", response);
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loading = false;
          this.errorFixed = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },

    getSessionOptions() {
      this.$store
        .dispatch(
          "getSessionOptions",
          this.$store.getters.getUserObject.attributes["custom:tenancy"]
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response.data) {
            this.operatingRooms = response.data.operatingRooms;
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.errorFixed = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    required(input: string, inputName: string) {
      if (input.length > 0) {
        if (inputName) this.error[inputName] = "";
        return true;
      } else {
        if (inputName)
          this.error[inputName] =
            inputName + " " + this.$t("error.validation.isRequired");
        return false;
      }
    },
  },
})
export default class SessionsAdd extends Vue {}
</script>

<style lang="scss" scoped>
.scroll-box {
  height: calc(100vh - 278px);
}
</style>
